import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  FormControl,
  FormLabel,
  IconButton,
  MenuItem,
  Select,
  ToggleButton,
  Typography,
} from "@mui/material";
import { InfoOutline } from "@mui/icons-material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { Tooltip } from "@mui/material";
import IOSSwitch from "@components/common/Switch";
import HPInput from "@components/common/Input";
import {
  GROUPED_CUSTOM_PALETTE,
  type GroupedSeriesColorEntry,
} from "@utils/grouped-bar-utils";

const TOGGLE_BUTTON_SX = {
  border: "1px solid #e0e0e0",
  borderRadius: "4px",
  p: 0.5,
};

// Grouped bar palettes are multi-color sequences (one color per series) plus a
// per-series Custom option. Single-color palettes are intentionally omitted —
// they would render every series in the same color.
const PALETTE_OPTIONS: { value: string; gradient: string }[] = [
  {
    value: "Multicolor",
    gradient:
      "linear-gradient(90deg, #57bfe7 0%, #57dfbe 25%, #ffb09c 50%, #a1a7df 75%, #c36179 100%)",
  },
  {
    value: "Gradient",
    gradient: "linear-gradient(90deg, #019EDB 0%, #00CF9C 100%)",
  },
  {
    value: "Grayscale",
    gradient: "linear-gradient(90deg, #f0f0f0 0%, #404040 100%)",
  },
  {
    value: GROUPED_CUSTOM_PALETTE,
    gradient:
      "linear-gradient(90deg, #e57373 0%, #81c784 33%, #64b5f6 66%, #ffd54f 100%)",
  },
];

interface GroupedStylingSectionProps {
  title: string;
  subtitle: string;
  titleBold: boolean;
  titleUnderline: boolean;
  colorPalette: string;
  seriesColorEntries: GroupedSeriesColorEntry[];
  manualColors: Record<string, string>;
  compactMode: boolean;
  onTitleChange: (value: string) => void;
  onSubtitleChange: (value: string) => void;
  onTitleBoldChange: (value: boolean) => void;
  onTitleUnderlineChange: (value: boolean) => void;
  onColorPaletteChange: (value: string) => void;
  onSeriesColorChange: (id: string, color: string | null) => void;
  onCompactModeChange: (value: boolean) => void;
}

/**
 * Styling & Customization for grouped bar: Chart Title, Subtitle and Color
 * Palette (multi-color sequences + Custom per-series). Labels placement, legend,
 * truncate, value-axis and compact mode are intentionally not supported here.
 */
const GroupedStylingSection: React.FC<GroupedStylingSectionProps> = ({
  title,
  subtitle,
  titleBold,
  titleUnderline,
  colorPalette,
  seriesColorEntries,
  manualColors,
  compactMode,
  onTitleChange,
  onSubtitleChange,
  onTitleBoldChange,
  onTitleUnderlineChange,
  onColorPaletteChange,
  onSeriesColorChange,
  onCompactModeChange,
}) => {
  // Tracks each series' color before the current (unconfirmed) edit, so Cancel
  // can revert — same pattern as the bar chart's per-bar picker.
  const [pendingOriginals, setPendingOriginals] = useState<
    Record<string, string | null>
  >({});

  useEffect(() => {
    if (colorPalette !== GROUPED_CUSTOM_PALETTE) setPendingOriginals({});
  }, [colorPalette]);

  const isCustom = colorPalette === GROUPED_CUSTOM_PALETTE;

  return (
    <Accordion
      defaultExpanded
      sx={{ boxShadow: "none", border: "1px solid #eee" }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Styling & Customization
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={{ position: "relative" }}>
            <HPInput
              label="Chart title"
              value={title}
              onChange={(e) => onTitleChange(e.target.value)}
            />
            <Box
              sx={{
                position: "absolute",
                bottom: "70%",
                right: 0,
                display: "flex",
                gap: 0.5,
              }}
            >
              <ToggleButton
                value="bold"
                selected={titleBold}
                onChange={() => onTitleBoldChange(!titleBold)}
                size="small"
                sx={TOGGLE_BUTTON_SX}
              >
                <FormatBoldIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton
                value="underline"
                selected={titleUnderline}
                onChange={() => onTitleUnderlineChange(!titleUnderline)}
                size="small"
                sx={TOGGLE_BUTTON_SX}
              >
                <FormatUnderlinedIcon fontSize="small" />
              </ToggleButton>
            </Box>
          </Box>

          <HPInput
            label="Subtitle"
            value={subtitle}
            onChange={(e) => onSubtitleChange(e.target.value)}
            placeholder="Optional"
          />

          {/* Color Palette */}
          <FormControl fullWidth size="small">
            <FormLabel className="text-[12px] text-[#262626]">
              Color palette
            </FormLabel>
            <Select
              value={colorPalette}
              onChange={(e) => onColorPaletteChange(e.target.value)}
              renderValue={(value) => {
                const opt = PALETTE_OPTIONS.find((o) => o.value === value);
                return (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      justifyContent: "space-between",
                      flexDirection: "row-reverse",
                    }}
                  >
                    <Box
                      sx={{
                        width: 24,
                        height: 12,
                        borderRadius: "16px",
                        background: opt?.gradient,
                      }}
                    />
                    {value}
                  </Box>
                );
              }}
            >
              {PALETTE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        borderRadius: "50%",
                        background: option.gradient,
                      }}
                    />
                    {option.value}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Per-series color pickers (Custom palette only) */}
          {isCustom && seriesColorEntries.length > 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {seriesColorEntries.map((entry) => {
                const currentColor =
                  manualColors[entry.id] || entry.fallbackColor;
                const isDirty = entry.id in pendingOriginals;
                return (
                  <Box
                    key={entry.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        flex: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        fontSize: "12px",
                      }}
                    >
                      {entry.displayName}
                    </Typography>
                    <input
                      type="color"
                      value={currentColor}
                      onChange={(e) => {
                        if (!(entry.id in pendingOriginals)) {
                          setPendingOriginals((prev) => ({
                            ...prev,
                            [entry.id]: manualColors[entry.id] ?? null,
                          }));
                        }
                        onSeriesColorChange(entry.id, e.target.value);
                      }}
                      style={{
                        width: 32,
                        height: 24,
                        cursor: "pointer",
                        border: isDirty ? "2px solid #1976d2" : "1px solid #ccc",
                        borderRadius: 4,
                        padding: 0,
                      }}
                    />
                    {isDirty && (
                      <>
                        <IconButton
                          size="small"
                          color="primary"
                          title="Apply color"
                          onClick={() =>
                            setPendingOriginals((prev) => {
                              const next = { ...prev };
                              delete next[entry.id];
                              return next;
                            })
                          }
                        >
                          <CheckIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          title="Cancel"
                          onClick={() => {
                            onSeriesColorChange(
                              entry.id,
                              pendingOriginals[entry.id],
                            );
                            setPendingOriginals((prev) => {
                              const next = { ...prev };
                              delete next[entry.id];
                              return next;
                            });
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}

          {/* Compact Mode */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography>Compact mode</Typography>
              <Tooltip title="Makes bars tighter, recommended for cases with many groups or series">
                <InfoOutline sx={{ fontSize: 16, color: "text.secondary" }} />
              </Tooltip>
            </Box>
            <IOSSwitch
              checked={compactMode}
              onChange={(e) => onCompactModeChange(e.target.checked)}
              size="small"
            />
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default GroupedStylingSection;
